import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { AnlaegDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/anlaeg-dto';
import moment from 'moment';
import { KomponentDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/komponent-dto';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/components/shared/confirm-dialog/confirm-dialog.component';
import { DriftformDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/driftform-dto';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AnlaegService } from 'src/app/api/services/anlaeg.service';
import { AnlaegUpdateDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/anlaeg-update-dto';
import { finalize, takeUntil } from 'rxjs/operators';
import { LoaderService } from 'src/app/services/loader.service';
import { Subject } from 'rxjs';
import { AnlaegEditComponent } from './anlaeg-edit/anlaeg-edit.component';
import { TilvalgDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/tilvalg-dto';
import { AnlaegsKomponentComponent } from '../../../create/anlaegs-komponent/anlaegs-komponent.component';
import { ProduktDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/produkt-dto';
import { AnlaegEditDialogData, AnlaegEditDialogResponse, AnlaegKomponentDialogData } from '../../../models';
import { ServiceAftaleService } from 'src/app/api/services';
import { KomponentTypeDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/komponent-type-dto';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { ProduktEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/produkt-enum';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-anlaeg',
  templateUrl: './anlaeg.component.html',
  styleUrls: ['./anlaeg.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AnlaegComponent implements OnInit {
  @Input() anlaeg: AnlaegDto;
  @Input() serviceAftaleAdresse: string;
  @Input() product: ProduktDto;
  @Input() tilvalg: TilvalgDto[];
  @Input() serviceaftale: ServiceaftaleDto;

  @Output() anlaegChange: EventEmitter<AnlaegDto> = new EventEmitter();

  driftformer: DriftformDto[];
  unsubscribe$ = new Subject<void>();
  komponentTyper: KomponentTypeDto[];
  komponentTypeMap: Map<number, string>;

  anlaegGarantiEndDate: Date;

  canWrite: boolean;

  hasRightsToChangeTilvalg: boolean;

  constructor(
    private serviceAftaleService: ServiceAftaleService,
    public dialog: MatDialog,
    private errorHandler: ErrorHandlerService,
    private anlaegService: AnlaegService,
    private notifications: NotificationService,
    private loader: LoaderService,
    private fb: UntypedFormBuilder,
    private auth: AuthService,
  ) {
    this.canWrite = this.auth.canWrite;
    this.hasRightsToChangeTilvalg = this.auth.isAdministrator || this.auth.isSuperuser;
  }

  ngOnInit(): void {
    this.anlaegGarantiEndDate = this.getGarantiEndDate(
      this.anlaeg.installationDate ? new Date(this.anlaeg.installationDate) : null,
      this.anlaeg.garantiPeriode,
    );
    this.serviceAftaleService.getAvailableKomponentTyper({ caller: EGIS_SERVICE_CALLER }).subscribe(
      (data) => {
        this.komponentTyper = data;
        this.komponentTypeMap = new Map<number, string>(data.map((x) => [x.id, x.text] as [number, string]));
      },
      (error: HttpErrorResponse) => {
        this.errorHandler.HandleError(error);
      },
    );
  }

  getGarantiEndDate(instaDate: Date, garanti: number): Date {
    return moment(instaDate).add(garanti, 'month').subtract(1, 'day').toDate();
  }

  addKomponent() {
    let data: AnlaegKomponentDialogData = {
      komponent: null,
      editMode: false,
      minInstallDate: this.anlaeg.installationDate ? new Date(this.anlaeg.installationDate) : null,
      komponentTyper: this.komponentTyper,
      komponenter: this.anlaeg.komponenter,
    };

    const dialogRef = this.dialog.open(AnlaegsKomponentComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (moment(result.controls.installationDate.value).isBefore(this.anlaeg.installationDate)) {
          this.anlaeg.installationDate = result.controls.installationDate.value.toOkDateString();
        }
        let anleagUpdateDto = this.mapToAnlaegUpdateDto(this.anlaeg);
        let komponentUpdate: KomponentDto = {
          garantiPeriode: result.controls.garantiPeriode.value,
          installationDate: result.controls.installationDate.value ? result.controls.installationDate.value.toOkDateString() : null,
          komponentTypeId: result.controls.komponentTypeId.value,
          model: result.controls.model.value,
          serienummer: result.controls.serienummer.value,
        };
        anleagUpdateDto.anlaeg.komponenter.push(komponentUpdate);
        this.updateAnlaeg(anleagUpdateDto);
      }
    });
  }

  editKomponent(komponent: any) {
    let komponentForm = this.fb.group({
      komponentTypeId: [komponent.komponentTypeId, [Validators.required]],
      model: [komponent.model, Validators.required],
      serienummer: [komponent.serienummer],
      installationDate: [komponent.installationDate ? new Date(komponent.installationDate) : null],
      garantiPeriode: [komponent.garantiPeriode, [Validators.max(1200), Validators.pattern('^\\d+$')]],
    });

    let data: AnlaegKomponentDialogData = {
      komponent: komponentForm,
      editMode: true,
      minInstallDate: this.anlaeg.installationDate ? new Date(this.anlaeg.installationDate) : null,
      komponentTyper: this.komponentTyper,
    };
    const dialogRef = this.dialog.open(AnlaegsKomponentComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: data,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (moment(result.controls.installationDate.value).isBefore(this.anlaeg.installationDate)) {
          this.anlaeg.installationDate = result.controls.installationDate.value.toOkDateString();
        }
        let anleagUpdateDto = this.mapToAnlaegUpdateDto(this.anlaeg);
        let komponentUpdate: KomponentDto = {
          garantiPeriode: result.controls.garantiPeriode.value,
          installationDate: result.controls.installationDate.value ? result.controls.installationDate.value.toOkDateString() : null,
          komponentTypeId: result.controls.komponentTypeId.value,
          model: result.controls.model.value,
          serienummer: result.controls.serienummer.value,
        };
        const index: number = anleagUpdateDto.anlaeg.komponenter.indexOf(komponent);
        if (index !== -1) {
          anleagUpdateDto.anlaeg.komponenter[index] = komponentUpdate;
          this.updateAnlaeg(anleagUpdateDto);
        }
      }
    });
  }

  deleteKomponent(komponent: any) {
    let dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Er du sikker?',
        body: 'Er du sikker på at du vil slette kompenenten med serienr: ' + komponent.serienummer + '?',
      },
    });

    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        let anleagUpdateDto = this.mapToAnlaegUpdateDto(this.anlaeg);
        const index: number = anleagUpdateDto.anlaeg.komponenter.indexOf(komponent);
        if (index !== -1) {
          anleagUpdateDto.anlaeg.komponenter.splice(index, 1);
          this.updateAnlaeg(anleagUpdateDto);
        }
      }
    });
  }

  editAnlaeg() {
    var data: AnlaegEditDialogData = {
      anlaeg: this.anlaeg,
      product: this.product,
      editMode: true,
    };

    const dialogRef = this.dialog.open(AnlaegEditComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: data,
    });

    dialogRef.afterClosed().subscribe((response: AnlaegEditDialogResponse) => {
      if (response) {
        this.updateAnlaeg(this.mapToAnlaegUpdateDto(response.anlaeg));
      }
    });
  }

  updateAnlaeg(anlaegUpdate: AnlaegUpdateDto) {
    this.loader.show();

    this.anlaegService
      .updateAnlaeg({ body: anlaegUpdate, caller: EGIS_SERVICE_CALLER })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (response: AnlaegDto) => {
          this.notifications.success('Anlægget er opdateret');

          this.anlaeg = response;
          this.anlaegGarantiEndDate = this.getGarantiEndDate(new Date(this.anlaeg.installationDate), this.anlaeg.garantiPeriode);
          this.anlaegChange.emit(response);
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  private mapToAnlaegUpdateDto(anlaeg: AnlaegDto) {
    let anlaegUpdateDto: AnlaegUpdateDto = {
      id: anlaeg.id,
      //Pass by value
      anlaeg: anlaeg,
    };

    return anlaegUpdateDto;
  }

  get isOlie() {
    return this.product.id === ProduktEnum.Olie;
  }
  get isVarmepumpe() {
    return this.product.id === ProduktEnum.Varmepumpe;
  }
}
